import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  ButtonGroup,
  Spinner,
  Select,
  TextField,
  Box,
  Badge,
  Banner,
  Divider,
  ProgressBar,
  Icon,
  Collapsible,
  EmptyState,
  Thumbnail,
  Link,
  InlineGrid,
  SkeletonBodyText,
  SkeletonDisplayText,
  useBreakpoints,
} from "@shopify/polaris";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  RefreshIcon,
  EditIcon,
  ViewIcon,
  ImageIcon,
  TextIcon,
  CalendarIcon,
  SearchIcon,
  LinkIcon,
  NoteIcon,
  InfoIcon,
  ExportIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@shopify/polaris-icons";
import { notifyReviewValue } from "./ReviewManager";

/* -------------------------------------------------------------------------- */
/* API                                                                        */
/* -------------------------------------------------------------------------- */

async function auditRequest(method, shop) {
  if (!window.shopify?.idToken) {
    throw new Error("Please open AnalyticsHero from Shopify Admin.");
  }
  const token = await window.shopify.idToken();
  return axios({
    method,
    url: "/analytics/blog-audit/",
    params: { shop },
    headers: { Authorization: `Bearer ${token}` },
  });
}

/* -------------------------------------------------------------------------- */
/* Config                                                                     */
/* -------------------------------------------------------------------------- */

const PLACEHOLDER_IMAGE =
  "https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png";

const AUDIT_STEPS = [
  "Fetching blog posts from your store",
  "Checking meta descriptions",
  "Analyzing images and alt text",
  "Checking product links",
  "Analyzing content length",
  "Finding heading issues",
];

// `badge` is the short text used on post rows (the long `label` is used on check rows).
const CHECK_META = {
  missing_alt_text: {
    icon: ImageIcon,
    iconTone: "caution",
    badgeTone: "warning",
    short: "Alt text",
    badge: "Missing alt text",
  },
  weak_headings: {
    icon: TextIcon,
    iconTone: "warning",
    badgeTone: "attention",
    short: "Headings",
    badge: "Weak headings",
  },
  outdated: {
    icon: CalendarIcon,
    iconTone: "info",
    badgeTone: "info",
    short: "Outdated",
    badge: "Outdated",
  },
};

const DEFAULT_META = {
  icon: AlertCircleIcon,
  iconTone: "caution",
  badgeTone: "attention",
  short: "Issue",
  badge: null,
};

const SORT_OPTIONS = [
  { label: "Most issues first", value: "issues" },
  { label: "Title (A–Z)", value: "title" },
  { label: "Longest first", value: "words_desc" },
  { label: "Shortest first", value: "words_asc" },
  { label: "Least recently updated", value: "oldest" },
];

const PAGE_SIZE = 5;
const POLL_MS = 3000;

/*
  CSS notes
  - Dashboard.jsx has global rules for `.Polaris-ButtonGroup` (black border, padding,
    margin-top) and `.Polaris-Button--pressed` (black !important). They leak into every
    Polaris ButtonGroup/pressed Button, which caused the black box around the post
    action buttons. The `.bha-root` rules below neutralise them for this card only.
    Best long-term fix: delete those two rules from Dashboard.jsx.
*/
const STYLES = `
.bha-root .Polaris-ButtonGroup { margin: 0; padding: 0; border: 0; border-radius: 0; width: auto; }
.bha-root .Polaris-Button--pressed,
.bha-root .Polaris-Button--pressed:hover,
.bha-root .Polaris-Button--pressed:focus,
.bha-root .Polaris-Button--pressed:active {
  background-color: var(--p-color-bg-fill-brand, #303030) !important;
  color: var(--p-color-text-brand-on-bg-fill, #fff) !important;
  border-color: var(--p-color-bg-fill-brand, #303030) !important;
}

.bha-clickable { cursor: pointer; transition: box-shadow .15s ease; border-radius: 12px; }
.bha-clickable:hover { box-shadow: var(--p-shadow-200, 0 2px 8px rgba(0,0,0,.12)); }
.bha-clickable:focus-visible { outline: 2px solid var(--p-color-border-focus, #005bd3); outline-offset: 2px; }
.bha-check { transition: box-shadow .2s ease; border-radius: 12px; }
.bha-check:hover { box-shadow: var(--p-shadow-100, 0 1px 4px rgba(0,0,0,.08)); }
.bha-check[data-open="true"] { box-shadow: var(--p-shadow-300, 0 4px 16px rgba(0,0,0,.10)); }
.bha-header { cursor: pointer; }

/* Check row header: stacks on small screens instead of overflowing */
.bha-check-head { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px 16px; align-items: center; }
.bha-check-meter { display: flex; align-items: center; gap: 16px; }
.bha-check-bar { width: 120px; }

/* Post row: thumbnail | info | actions */
.bha-post-row { border-radius: 12px; transition: box-shadow .15s ease; }
.bha-post-row:hover { box-shadow: var(--p-shadow-100, 0 1px 4px rgba(0,0,0,.08)); }
.bha-post-row:focus-within { box-shadow: 0 0 0 1px var(--p-color-border-focus, #005bd3); }
.bha-post { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 16px; align-items: center; }
.bha-post-actions { display: flex; gap: 8px; justify-content: flex-end; }

/* Running audit step list */
.bha-steps { display: flex; flex-direction: column; gap: 16px; }
.bha-step { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.bha-step-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.bha-dot { width: 14px; height: 14px; border-radius: 50%; border: 2px solid #8a8a8a; box-sizing: border-box; flex: none; position: relative; }
.bha-dot--done { background: #4a4a4a; border-color: #4a4a4a; }
.bha-dot--current { background: #005bd3; border-color: #005bd3; }
.bha-dot--done::after, .bha-dot--current::after {
  content: ""; position: absolute; left: 3px; top: 0px; width: 3px; height: 6px;
  border: solid #fff; border-width: 0 1.5px 1.5px 0; transform: rotate(45deg);
}
.bha-step-note { text-align: right; flex: none; }

/* Toolbar: filter tabs on the left, search + sort on the right */
.bha-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; }
.bha-toolbar-right { display: flex; flex: 1 1 360px; gap: 12px; justify-content: flex-end; min-width: 0; }
.bha-search { flex: 1 1 220px; max-width: 340px; min-width: 0; }
.bha-sort { flex: 0 0 200px; }

/* Post image gallery */
.bha-img-frame { aspect-ratio: 4 / 3; overflow: hidden; border-radius: 8px; background: #f1f1f1; }
.bha-img-frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
.bha-mini { width: 48px; height: 48px; flex: none; border-radius: 8px; overflow: hidden; background: #f1f1f1; border: 1px solid var(--p-color-border, #dcdcdc); }
.bha-mini--missing { border: 2px solid var(--p-color-border-critical, #e0b3b2); }
.bha-mini img { width: 100%; height: 100%; object-fit: cover; display: block; }
.bha-img-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }

@media (max-width: 767px) {
  /* Toolbar: stack tabs, search and sort full width.
     (flex-basis would become a height in a column, so reset it to auto.) */
  .bha-toolbar { flex-direction: column; align-items: stretch; gap: 12px; }
  .bha-toolbar-right { flex: 0 0 auto; flex-direction: column; align-items: stretch; justify-content: flex-start; width: 100%; gap: 12px; }
  .bha-search, .bha-sort { flex: 0 0 auto; width: 100%; max-width: none; }
  .bha-root .bha-toolbar .Polaris-ButtonGroup { width: 100%; }
  .bha-root .bha-toolbar .Polaris-ButtonGroup__Item { flex: 1 1 0; min-width: 0; }
  .bha-root .bha-toolbar .Polaris-ButtonGroup__Item .Polaris-Button { width: 100%; justify-content: center; }
  .bha-check-head { grid-template-columns: minmax(0, 1fr); }
  .bha-check-meter { flex-wrap: wrap; justify-content: space-between; }
  .bha-check-bar { flex: 1 1 100%; width: auto; order: 3; }
  .bha-post { grid-template-columns: auto minmax(0, 1fr); align-items: start; }
  .bha-post-actions { grid-column: 1 / -1; justify-content: stretch; flex-wrap: wrap; }
  .bha-post-actions .bha-action:first-child { flex: 1 1 100%; }
  .bha-post-actions .bha-action { flex: 1 1 0; min-width: 0; }
  .bha-step { flex-wrap: wrap; }
  .bha-step-note { text-align: left; width: 100%; padding-left: 26px; }
}
@media (prefers-reduced-motion: reduce) {
  .bha-clickable, .bha-check, .bha-post-row { transition: none; }
}
`;

function metaFor(key, label = "") {
  if (CHECK_META[key]) return CHECK_META[key];
  const text = `${key} ${label}`.toLowerCase();
  if (/alt/.test(text)) return CHECK_META.missing_alt_text;
  if (/heading/.test(text)) return CHECK_META.weak_headings;
  if (/outdated|updated|stale|old/.test(text)) return CHECK_META.outdated;
  if (/meta/.test(text))
    return {
      icon: SearchIcon,
      iconTone: "caution",
      badgeTone: "warning",
      short: "Meta description",
      badge: "No meta description",
    };
  if (/product|link/.test(text))
    return {
      icon: LinkIcon,
      iconTone: "caution",
      badgeTone: "warning",
      short: "Product links",
      badge: "No product links",
    };
  if (/word|short|length/.test(text))
    return {
      icon: NoteIcon,
      iconTone: "info",
      badgeTone: "attention",
      short: "Word count",
      badge: "Under 300 words",
    };
  return DEFAULT_META;
}

function formatDate(value, options) {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, options);
}

function formatEta(seconds) {
  if (seconds == null || !isFinite(seconds)) return "";
  if (seconds < 60) return "Less than a minute remaining";
  const m = Math.ceil(seconds / 60);
  return `~ ${m} ${m === 1 ? "minute" : "minutes"} remaining`;
}
const collapseKey = (shop) => `analyticshero:blog-audit-collapsed:${shop || "default"}`;

function readCollapsed(shop) {
  try {
    return window.localStorage.getItem(collapseKey(shop)) === "1";
  } catch {
    return false; // no saved value means expanded, which is the default after install
  }
}

function saveCollapsed(shop, value) {
  try {
    window.localStorage.setItem(collapseKey(shop), value ? "1" : "0");
  } catch {
    /* best effort */
  }
}
/* -------------------------------------------------------------------------- */
/* Small building blocks                                                      */
/* -------------------------------------------------------------------------- */

/* One summary card in the top row */
function SummaryCard({ icon, iconTone, iconBg, value, label, helper }) {
  return (
    <Box
      padding="400"
      background="bg-surface"
      borderWidth="025"
      borderColor="border"
      borderRadius="300"
    >
      <BlockStack gap="300">
        <InlineStack gap="300" blockAlign="center" wrap={false}>
          <Box padding="200" background={iconBg} borderRadius="full">
            <Icon source={icon} tone={iconTone} />
          </Box>
          <Text as="p" variant="heading2xl">
            {value}
          </Text>
        </InlineStack>
        <BlockStack gap="050">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {label}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {helper}
          </Text>
        </BlockStack>
      </BlockStack>
    </Box>
  );
}

/* Progress panel shown while the audit runs (initial run and "Re-run audit") */
function RunningAuditPanel({ stepIndex, percent, etaText, detail, done, onCancel, cancelling }) {
  return (
    <Box
      padding="500"
      background="bg-surface"
      borderWidth="025"
      borderColor="border"
      borderRadius="300"
    >
      <BlockStack gap="500">
        <InlineStack align="space-between" blockAlign="start" gap="300">
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            {done ? (
              <Icon source={CheckCircleIcon} tone="success" />
            ) : (
              <Spinner size="small" accessibilityLabel="Running audit" />
            )}
            <BlockStack gap="050">
              <Text as="h3" variant="headingMd">
                {done ? "Audit complete" : "Running blog content audit"}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                We're analyzing your blog posts. This may take a few minutes.
              </Text>
            </BlockStack>
          </InlineStack>
          {onCancel && (
            <Button onClick={onCancel} loading={cancelling}>
              Cancel
            </Button>
          )}
        </InlineStack>

        <InlineStack gap="300" blockAlign="center" wrap={false}>
          <div style={{ flex: 1 }}>
            <ProgressBar progress={percent} size="medium" tone="highlight" animated />
          </div>
          <Text as="p" variant="bodySm" fontWeight="semibold">
            {`${percent}%`}
          </Text>
        </InlineStack>

        <div className="bha-steps" role="status" aria-live="polite">
          {AUDIT_STEPS.map((label, i) => {
            const state = i < stepIndex ? "done" : i === stepIndex ? "current" : "todo";
            return (
              <div className="bha-step" key={label}>
                <div className="bha-step-left">
                  <span
                    className={`bha-dot ${state === "todo" ? "" : `bha-dot--${state}`}`}
                    aria-hidden="true"
                  />
                  <Text
                    as="span"
                    variant="bodyMd"
                    fontWeight={state === "current" ? "semibold" : "regular"}
                    tone={state === "todo" ? "subdued" : undefined}
                  >
                    {label}
                  </Text>
                </div>
                {state === "current" && (
                  <div className="bha-step-note">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {`Step ${i + 1} of ${AUDIT_STEPS.length} · ${percent}%`}
                    </Text>
                    {detail && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {detail}
                      </Text>
                    )}
                    {etaText && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {etaText}
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Banner tone="info" icon={InfoIcon}>
          <Text as="p">You can leave this page. We'll notify you when the audit is complete.</Text>
        </Banner>
      </BlockStack>
    </Box>
  );
}

function IssueDetail({ post, issueKey }) {
  if (issueKey === "missing_alt_text") {
    const count = post.missing_inline_alt_count || 0;
    return (
      <BlockStack gap="100">
        <Text as="p" fontWeight="medium">
          Missing alt text
        </Text>
        <Text as="p" tone="subdued">
          {count} inline {count === 1 ? "image needs" : "images need"} alt text
          {post.missing_featured_alt ? " and the featured image needs it too" : ""}.
        </Text>
      </BlockStack>
    );
  }

  if (issueKey === "weak_headings") {
    const reasons = post.heading_reasons || [];
    return (
      <BlockStack gap="100">
        <Text as="p" fontWeight="medium">
          Heading structure
        </Text>
        {reasons.length > 0 ? (
          <BlockStack gap="050">
            {reasons.map((reason) => (
              <InlineStack key={reason} gap="200" wrap={false} blockAlign="start">
                <Box paddingBlockStart="100">
                  <Icon source={AlertCircleIcon} tone="caution" />
                </Box>
                <Text as="p" tone="subdued">
                  {reason}
                </Text>
              </InlineStack>
            ))}
          </BlockStack>
        ) : (
          <Text as="p" tone="subdued">
            The heading structure could be improved.
          </Text>
        )}
      </BlockStack>
    );
  }

  if (issueKey === "outdated" && post.updated_at) {
    return (
      <BlockStack gap="100">
        <Text as="p" fontWeight="medium">
          Last updated
        </Text>
        <Text as="p" tone="subdued">
          {formatDate(post.updated_at, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Text>
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="100">
      <Text as="p" fontWeight="medium">
        Flagged by this check
      </Text>
      <Text as="p" tone="subdued">
        {issueKey.includes("word") || issueKey.includes("short")
          ? `This post has ${post.word_count} words. Aim for at least 300.`
          : "Open the post in Shopify and apply the recommendation above."}
      </Text>
    </BlockStack>
  );
}

/* ---------------------------- Post images ---------------------------- */

function fixUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  return trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;
}

function normalizeImage(img) {
  if (!img) return null;
  if (typeof img === "string") {
    const url = fixUrl(img);
    return url ? { url, alt: "" } : null;
  }
  const url = fixUrl(img.url || img.src || img.original_src || img.image_url);
  if (!url) return null;
  return { url, alt: img.alt ?? img.alt_text ?? img.altText ?? "" };
}

// Pulls <img> tags out of the post body HTML (used when the API sends the body)
function parseBodyImages(html) {
  if (!html || typeof html !== "string" || typeof DOMParser === "undefined") return [];
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return Array.from(doc.querySelectorAll("img"))
      .map((el) =>
        normalizeImage({
          url: el.getAttribute("src") || el.getAttribute("data-src"),
          alt: el.getAttribute("alt") || "",
        })
      )
      .filter(Boolean);
  } catch {
    return [];
  }
}

/* Every image of a post: featured image first, then the images inside the content.
   Accepts any of these from the API:
   - featured_image_url (+ featured_image_alt), or featured_image / image as { src|url, alt }
   - inline_images / images: [{ url|src, alt }] (or plain URLs)
   - body_html / body / content: the post HTML (images are read from it) */
function getPostImages(post) {
  const featuredObj =
    post.featured_image && typeof post.featured_image === "object"
      ? post.featured_image
      : post.image && typeof post.image === "object"
        ? post.image
        : null;
  const featuredUrl = fixUrl(
    post.featured_image_url ||
    featuredObj?.url ||
    featuredObj?.src ||
    (typeof post.featured_image === "string" ? post.featured_image : "") ||
    post.image_url
  );
  const featuredAlt = post.featured_image_alt ?? featuredObj?.alt ?? "";

  const list = [];
  const seen = new Set();
  const add = (img, featured = false) => {
    if (!img || seen.has(img.url)) return;
    seen.add(img.url);
    list.push({ ...img, featured });
  };

  if (featuredUrl) add({ url: featuredUrl, alt: featuredAlt }, true);

  const fromApi = Array.isArray(post.inline_images)
    ? post.inline_images
    : Array.isArray(post.images)
      ? post.images
      : null;
  const inline = fromApi
    ? fromApi.map(normalizeImage).filter(Boolean)
    : parseBodyImages(post.body_html || post.body || post.content);
  inline.forEach((img) => add(img));

  return list;
}

/* <img> that shows an icon instead of a broken image */
function PostImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="bha-img-fallback">
        <Icon source={ImageIcon} tone="subdued" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

/* Gallery of every image in a post. Images missing alt text are listed first. */
function PostImages({ images }) {
  if (images.length === 0) {
    return (
      <Text as="p" variant="bodySm" tone="subdued">
        No images were found in this post.
      </Text>
    );
  }

  const sorted = [...images].sort(
    (a, b) => Number(!!a.alt?.trim()) - Number(!!b.alt?.trim())
  );

  return (
    <BlockStack gap="200">
      <Text as="p" variant="bodySm" fontWeight="semibold">
        {`Images in this post (${images.length})`}
      </Text>
      <InlineGrid columns={{ xs: 2, sm: 3, md: 4 }} gap="300">
        {sorted.map((img, i) => {
          const missing = !img.alt?.trim();
          return (
            <Box
              key={`${img.url}-${i}`}
              padding="200"
              background="bg-surface"
              borderWidth="025"
              borderColor={missing ? "border-critical" : "border"}
              borderRadius="200"
            >
              <BlockStack gap="150">
                <div className="bha-img-frame">
                  <PostImage src={img.url} alt={img.alt} />
                </div>
                <InlineStack gap="100">
                  {img.featured && <Badge size="small">Featured</Badge>}
                  <Badge size="small" tone={missing ? "critical" : "success"}>
                    {missing ? "Missing alt text" : "Alt text set"}
                  </Badge>
                </InlineStack>
                {!missing && (
                  <Text as="p" variant="bodySm" tone="subdued" truncate>
                    {img.alt}
                  </Text>
                )}
              </BlockStack>
            </Box>
          );
        })}
      </InlineGrid>
    </BlockStack>
  );
}

function PostRow({ post, issueKey, labels, expanded, onToggle, reviewed, onToggleReviewed }) {
  const { smDown } = useBreakpoints();
  const detailsId = `post-details-${post.id}`;
  const images = useMemo(() => getPostImages(post), [post]);
  const MAX_STRIP = 4;

  return (
    <div className="bha-post-row">
      <Box
        padding="400"
        background={reviewed ? "bg-surface-success" : "bg-surface"}
        borderWidth="025"
        borderColor={expanded ? "border-emphasis" : "border"}
        borderRadius="300"
      >
        <BlockStack gap="300">
          <div className="bha-post">
            <Thumbnail
              source={images[0]?.url || PLACEHOLDER_IMAGE}
              alt={post.title}
              size={smDown ? "small" : "medium"}
            />

            <BlockStack gap="100">
              <Text as="h4" variant="headingSm" breakWord>
                {post.title}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {post.blog_title} · {post.word_count} words
              </Text>
              <InlineStack gap="100">
                {reviewed && (
                  <Badge tone="success" size="small" icon={CheckCircleIcon}>
                    Fixed
                  </Badge>
                )}
                {(post.issues || []).map((key) => {
                  const meta = metaFor(key, labels[key]);
                  return (
                    <Badge key={key} tone={meta.badgeTone} size="small">
                      {meta.badge || labels[key] || meta.short}
                    </Badge>
                  );
                })}
              </InlineStack>


            </BlockStack>

            <div className="bha-post-actions">
              <div className="bha-action">
                <Button
                  fullWidth
                  icon={CheckCircleIcon}
                  variant={reviewed ? "primary" : "secondary"}
                  tone={reviewed ? "success" : undefined}
                  onClick={onToggleReviewed}
                  accessibilityLabel={
                    reviewed
                      ? `Mark ${post.title} as not fixed`
                      : `Mark ${post.title} as fixed`
                  }
                >
                  {reviewed ? "Fixed" : "Mark as fixed"}
                </Button>
              </div>
              <div className="bha-action">
                <Button
                  fullWidth
                  url={post.edit_url}
                  external
                  icon={EditIcon}
                  accessibilityLabel={`Edit ${post.title} in Shopify`}
                >
                  Edit
                </Button>
              </div>
              <div className="bha-action">
                <Button
                  fullWidth
                  onClick={onToggle}
                  disclosure={expanded ? "up" : "down"}
                  ariaExpanded={expanded}
                  ariaControls={detailsId}
                >
                  Details
                </Button>
              </div>
            </div>
          </div>

          <Collapsible
            open={expanded}
            id={detailsId}
            transition={{ duration: "200ms", timingFunction: "ease-in-out" }}
          >
            <BlockStack gap="300">
              <Divider />
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <IssueDetail post={post} issueKey={issueKey} />
              </Box>
              <PostImages images={images} />
              <InlineStack gap="200">
                <Button url={post.edit_url} external icon={EditIcon} size="slim">
                  Edit in Shopify
                </Button>
                <Button
                  url={post.storefront_url}
                  external
                  icon={ViewIcon}
                  variant="tertiary"
                  size="slim"
                >
                  View post
                </Button>
              </InlineStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Box>
    </div>
  );
}

/* Panel that opens directly under the selected check */
function AffectedPostsPanel({
  definition,
  checkPostCount,
  reviewedCount,
  posts,
  visiblePosts,
  remaining,
  query,
  onQueryChange,
  labels,
  issueKey,
  expandedPost,
  onToggleExpanded,
  reviewed,
  onToggleReviewed,
  onShowMore,
  showDivider = true,
  title = "Affected posts",
  onClose,
}) {
  const reviewedPercent =
    checkPostCount > 0 ? Math.round((reviewedCount / checkPostCount) * 100) : 0;

  return (
    <BlockStack gap="400">
      {showDivider && <Divider />}

      <InlineStack align="space-between" blockAlign="center" gap="300">
        <BlockStack gap="050">
          <Text as="h4" variant="headingSm">
            {title}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {reviewedCount} of {checkPostCount} fixed this session
          </Text>
        </BlockStack>
        <InlineStack gap="300" blockAlign="center">
          <div style={{ width: 140, maxWidth: "100%" }}>
            <ProgressBar progress={reviewedPercent} size="small" tone="success" animated={false} />
          </div>
          {onClose && (
            <Button variant="tertiary" onClick={onClose}>
              Close
            </Button>
          )}
        </InlineStack>
      </InlineStack>

      {definition.recommendation && (
        <Banner tone="info" title="How to fix it">
          <Text as="p">{definition.recommendation}</Text>
        </Banner>
      )}

      {posts.length === 0 ? (
        <Box padding="500" background="bg-surface-secondary" borderRadius="300">
          <BlockStack gap="200" inlineAlign="center">
            <Text as="p" fontWeight="medium">
              No posts match
            </Text>
            <Text as="p" tone="subdued">
              Try a different search term.
            </Text>
            {query && <Button onClick={() => onQueryChange("")}>Clear search</Button>}
          </BlockStack>
        </Box>
      ) : (
        <BlockStack gap="300">
          {visiblePosts.map((post) => (
            <PostRow
              key={post.id}
              post={post}
              issueKey={issueKey}
              labels={labels}
              expanded={expandedPost === post.id}
              onToggle={() => onToggleExpanded(post.id)}
              reviewed={!!reviewed[`${issueKey}:${post.id}`]}
              onToggleReviewed={() => onToggleReviewed(`${issueKey}:${post.id}`)}
            />
          ))}

          {remaining > 0 && (
            <InlineStack align="center">
              <Button onClick={onShowMore}>
                {`Show ${Math.min(remaining, PAGE_SIZE)} more (${remaining} remaining)`}
              </Button>
            </InlineStack>
          )}
        </BlockStack>
      )}
    </BlockStack>
  );
}

/* One check = header row + collapsible panel (opens right below this row) */
function CheckRow({ check, total, selected, onToggle, children }) {
  const meta = metaFor(check.key, check.label);
  const passed = check.count === 0;
  const percent = total > 0 ? Math.round((check.count / total) * 100) : 0;
  const ref = useRef(null);

  useEffect(() => {
    if (!selected) return undefined;
    const t = setTimeout(
      () => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      180
    );
    return () => clearTimeout(t);
  }, [selected]);

  return (
    <div ref={ref} className="bha-check" data-open={selected}>
      <Box
        padding="400"
        background={selected ? "bg-surface-selected" : "bg-surface"}
        borderWidth={selected ? "050" : "025"}
        borderColor={selected ? "border-emphasis" : "border"}
        borderRadius="300"
      >
        <BlockStack gap="400">
          {/* Clicking anywhere on the header toggles the panel. The button only
              carries semantics; its click bubbles up to this handler. */}
          <div
            className={passed ? undefined : "bha-header"}
            onClick={passed ? undefined : onToggle}
          >
            <div className="bha-check-head">
              <InlineStack gap="300" blockAlign="center" wrap={false}>
                <Box
                  padding="200"
                  background={
                    passed
                      ? "bg-surface-success"
                      : meta.iconTone === "info"
                        ? "bg-surface-info"
                        : "bg-surface-warning"
                  }
                  borderRadius="300"
                >
                  <Icon
                    source={passed ? CheckCircleIcon : meta.icon}
                    tone={passed ? "success" : meta.iconTone}
                  />
                </Box>
                <BlockStack gap="050">
                  <Text as="p" fontWeight="semibold">
                    {check.label}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {`${check.count} of ${total} ${check.label.toLowerCase()}`}
                  </Text>
                </BlockStack>
              </InlineStack>

              <div className="bha-check-meter">
                <div className="bha-check-bar">
                  <ProgressBar
                    progress={percent}
                    size="small"
                    tone={passed ? "success" : "highlight"}
                    animated={false}
                  />
                </div>
                {passed ? (
                  <Badge tone="success" icon={CheckCircleIcon}>
                    Passed
                  </Badge>
                ) : (
                  <>
                    <Badge tone={meta.badgeTone}>
                      {`${check.count} ${check.count === 1 ? "post" : "posts"}`}
                    </Badge>
                    <Button
                      variant={selected ? "primary" : "secondary"}
                      disclosure={selected ? "up" : "down"}
                      ariaExpanded={selected}
                      ariaControls={`check-panel-${check.key}`}
                    >
                      {selected ? "Hide posts" : "View posts"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {!passed && (
            <Collapsible
              open={selected}
              id={`check-panel-${check.key}`}
              transition={{ duration: "250ms", timingFunction: "ease-in-out" }}
            >
              {selected ? children : null}
            </Collapsible>
          )}
        </BlockStack>
      </Box>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

export default function BlogAuditCard({ shopOrigin }) {
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");
  const [selectedCheck, setSelectedCheck] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedPost, setExpandedPost] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("issues");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewed, setReviewed] = useState({});
  const [stopped, setStopped] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const staleRef = useRef(null);
  // The progress panel is driven by `displayStep`, which advances one step at a time
  // no matter how fast the API answers. `animating` keeps the panel on screen until
  // every step has been shown, then the results appear.
  const [displayStep, setDisplayStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const { smDown } = useBreakpoints();
  const [collapsed, setCollapsed] = useState(() => readCollapsed(shopOrigin));

  useEffect(() => {
    setCollapsed(readCollapsed(shopOrigin)); // shopOrigin can arrive after first render
  }, [shopOrigin]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    saveCollapsed(shopOrigin, next);
  }
  useEffect(() => {
    if (!shopOrigin || stopped) return undefined;
    let cancelled = false;
    let timer;
    let emptyResponseCount = 0;
    const MAX_EMPTY_RESPONSES = 3;
    let staleTries = 0;
    // Keep the progress panel on screen if an audit is already running (e.g. after "Re-run audit")
    setAudit((prev) => (prev && ["pending", "running"].includes(prev.status) ? prev : null));
    setError("");

    async function load() {
      try {
        let response = await auditRequest("get", shopOrigin);
        if (cancelled) return;
        if (response.data.status === "not_started") {
          setAudit({ status: "pending", scanned_count: 0, checks: [], posts: [] });
          await auditRequest("post", shopOrigin);
          if (cancelled) return;
          response = await auditRequest("get", shopOrigin);
        }
        if (cancelled) return;

        const data = response.data;

        // Stop polling if the API returns an empty or invalid response.
        if (!data || !data.status) {
          emptyResponseCount += 1;

          if (emptyResponseCount >= MAX_EMPTY_RESPONSES) {
            setAudit(null);
            setError("No audit data received. Please try running the audit again.");
            return;
          }

          timer = setTimeout(load, POLL_MS);
          return;
        }

        // After "Re-run audit" the first GET can still return the previous finished
        // audit. Ignore it and keep polling until the new run shows up.
        if (
          staleRef.current &&
          data.status === "complete" &&
          data.completed_at === staleRef.current &&
          staleTries < 10
        ) {
          staleTries += 1;
          timer = setTimeout(load, POLL_MS);
          return;
        }
        staleRef.current = null;

        emptyResponseCount = 0;
        setAudit(data);
        setError("");

        if (["pending", "running"].includes(data.status)) {
          timer = setTimeout(load, POLL_MS);
        }
      } catch (err) {
        if (!cancelled) {
          setAudit((prev) =>
            prev && ["pending", "running"].includes(prev.status) ? null : prev
          );
          setError(
            err.response?.data?.message ||
            err.message ||
            "Unable to load the blog audit."
          );
        }
      }
    }
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shopOrigin, reloadKey, stopped]);

  const auditStatus = audit?.status;
  const auditStep = audit?.current_step;
  const isRunning = auditStatus === "pending" || auditStatus === "running";
  const isComplete = auditStatus === "complete";

  // Start the panel whenever a run is in progress (first load or "Re-run audit")
  useEffect(() => {
    if (isRunning && !animating) {
      setDisplayStep(0);
      setAnimating(true);
    }
  }, [isRunning, animating]);

  // Run was cancelled, failed, or errored: drop the panel
  useEffect(() => {
    if (animating && !isRunning && !isComplete) {
      setAnimating(false);
      setDisplayStep(0);
    }
  }, [animating, isRunning, isComplete]);

  // Advance one step at a time.
  // - While the API is still running: slowly, and never past the last step (or the
  //   backend's current_step, if it sends one).
  // - Once the API says "complete": quickly, so each remaining step is still seen,
  //   then the results are revealed.
  useEffect(() => {
    if (!animating) return undefined;
    const total = AUDIT_STEPS.length;

    if (displayStep >= total) {
      const t = setTimeout(() => {
        setAnimating(false);
        setDisplayStep(0);
      }, 900);
      return () => clearTimeout(t);
    }

    const cap = isComplete
      ? total
      : Number.isInteger(auditStep)
        ? Math.min(total - 1, Math.max(0, auditStep - 1))
        : total - 1;
    if (displayStep >= cap) return undefined; // wait for the API

    const t = setTimeout(
      () => setDisplayStep((step) => step + 1),
      isComplete ? 800 : 2500
    );
    return () => clearTimeout(t);
  }, [animating, displayStep, isComplete, auditStep]);

  async function restart() {
    setBusy(true);
    setError("");
    try {
      staleRef.current = audit?.completed_at || null;
      await auditRequest("post", shopOrigin);
      setSelectedCheck("");
      setExpandedPost(null);
      setQuery("");
      setReviewed({});
      setVisibleCount(PAGE_SIZE);
      // Show the progress panel immediately instead of waiting for the first poll
      setAudit({ status: "pending", scanned_count: 0, checks: [], posts: [] });
      setDisplayStep(0);
      setAnimating(true);
      setStopped(false);
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Unable to start the audit."
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancelAudit() {
    setCancelling(true);
    setStopped(true); // stops polling and prevents auto-restart
    try {
      await auditRequest("delete", shopOrigin); // needs a DELETE handler on the backend
    } catch {
      /* best effort */
    }
    setAudit((prev) => ({ ...(prev || {}), status: "cancelled" }));
    setCancelling(false);
  }

  function selectCheck(key) {
    setSelectedCheck(key);
    setExpandedPost(null);
    setVisibleCount(PAGE_SIZE);

    if (key && audit?.status === "complete") {
      notifyReviewValue(shopOrigin, "audit_recommendation_opened", {
        check: key,
      });
    }
  }

  function toggleReviewed(key) {
    setReviewed((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleQueryChange(value) {
    setQuery(value);
    setVisibleCount(PAGE_SIZE);
  }

  // Downloads every post that has issues as a CSV file
  function exportAudit() {
    const safe = (value) => {
      let text = String(value ?? "");
      if (/^[=+\-@]/.test(text)) text = `'${text}`; // avoid spreadsheet formula injection
      return `"${text.replace(/"/g, '""')}"`;
    };
    const rows = [["Blog", "Post", "Issues", "Word count", "Last updated", "Edit URL"]];
    postsWithIssues.forEach((post) => {
      rows.push([
        post.blog_title,
        post.title,
        (post.issues || []).map((key) => labels[key] || key).join("; "),
        post.word_count,
        post.updated_at || "",
        post.edit_url || "",
      ]);
    });
    const csv = rows.map((row) => row.map(safe).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `blog-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /* ----------------------------- Derived data ----------------------------- */

  const running = audit && ["pending", "running"].includes(audit.status);
  const complete = audit?.status === "complete";
  const cancelled = audit?.status === "cancelled";
  const scanned = audit?.scanned_count || 0;
    const showTrackingBanner = audit?.first_insights_ready === false;

  /* Progress shown to the user comes from `displayStep` (see the sequencer above),
     so steps complete one after another instead of jumping. */
  const totalPosts =
    Number(audit?.total_count ?? audit?.total_posts ?? audit?.total ?? 0) || 0;
  const hasTotal = totalPosts > 0;
  const stepsDone = displayStep >= AUDIT_STEPS.length;
  const stepIndex = displayStep;
  const progressPercent = stepsDone
    ? 100
    : Math.round(((displayStep + 0.5) / AUDIT_STEPS.length) * 100);

  const progressDetail = hasTotal
    ? `${scanned} of ${totalPosts} posts checked`
    : scanned > 0
      ? `${scanned} ${scanned === 1 ? "post" : "posts"} checked so far`
      : "";

  const etaSeconds =
    audit?.eta_seconds ?? (AUDIT_STEPS.length - displayStep) * 10;

  const findings = useMemo(
    () => (audit?.checks || []).filter((check) => check.count > 0),
    [audit]
  );
  const allChecks = useMemo(
    () =>
      [...(audit?.checks || [])].sort(
        (a, b) => Number(a.count === 0) - Number(b.count === 0)
      ),
    [audit]
  );
  const passedChecks = allChecks.filter((c) => c.count === 0).length;
  const searchTerm = query.trim().toLowerCase();
  const postMatchesSearch = (post) =>
    !searchTerm ||
    post.title?.toLowerCase().includes(searchTerm) ||
    post.blog_title?.toLowerCase().includes(searchTerm);

  // Tabs (All / Issues / Passed), then the search box, then the sort dropdown
  const visibleChecks = allChecks
    .filter((c) =>
      statusFilter === "issues"
        ? c.count > 0
        : statusFilter === "passed"
          ? c.count === 0
          : true
    )
    .filter((c) =>
      !searchTerm
        ? true
        : c.count > 0 &&
        (audit?.posts || []).some(
          (post) => post.issues?.includes(c.key) && postMatchesSearch(post)
        )
    )
    .sort((a, b) => {
      if (sort === "title") return (a.label || "").localeCompare(b.label || "");
      if (sort === "issues") return b.count - a.count;
      return 0;
    });

  const labels = useMemo(
    () => Object.fromEntries((audit?.checks || []).map((c) => [c.key, c.label])),
    [audit]
  );

  const postsWithIssues = useMemo(
    () => (audit?.posts || []).filter((post) => (post.issues || []).length > 0),
    [audit]
  );

  // "Fixed" = posts the user has marked as fixed this session
  const issuesFixed = Object.values(reviewed).filter(Boolean).length;
  const postsFixed = postsWithIssues.filter((post) =>
    (post.issues || []).every((key) => reviewed[`${key}:${post.id}`])
  ).length;
  // Same calculation as the old "Content health": posts with no issues out of all
  // scanned posts. Posts marked as fixed this session count as healthy too.
  const cleanPosts =
    scanned > 0 ? Math.min(scanned, scanned - postsWithIssues.length + postsFixed) : 0;
  const completionPercent = scanned > 0 ? Math.round((cleanPosts / scanned) * 100) : 100;
  const canExport =
    !animating && (complete || audit?.status === "failed") && postsWithIssues.length > 0;
  const hasResults =
    (complete || audit?.status === "failed") && scanned > 0 && !animating;
  const selectedDefinition = findings.find((c) => c.key === selectedCheck);

  const checkPosts = useMemo(
    () => (audit?.posts || []).filter((post) => post.issues?.includes(selectedCheck)),
    [audit, selectedCheck]
  );
  const reviewedCount = checkPosts.filter(
    (post) => reviewed[`${selectedCheck}:${post.id}`]
  ).length;

  const affectedPosts = useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = checkPosts.filter(
      (post) =>
        !term ||
        post.title?.toLowerCase().includes(term) ||
        post.blog_title?.toLowerCase().includes(term)
    );

    const sorted = [...list];
    switch (sort) {
      case "title":
        sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
      case "words_desc":
        sorted.sort((a, b) => (b.word_count || 0) - (a.word_count || 0));
        break;
      case "words_asc":
        sorted.sort((a, b) => (a.word_count || 0) - (b.word_count || 0));
        break;
      case "oldest":
        sorted.sort(
          (a, b) =>
            new Date(a.updated_at || 0).getTime() -
            new Date(b.updated_at || 0).getTime()
        );
        break;
      default:
        sorted.sort((a, b) => b.issues.length - a.issues.length);
    }
    return sorted;
  }, [checkPosts, query, sort]);

  const visiblePosts = affectedPosts.slice(0, visibleCount);
  const remaining = affectedPosts.length - visiblePosts.length;

  /* -------------------------------- Render -------------------------------- */

  return (
    <Box paddingBlockEnd="400">
      <style>{STYLES}</style>
      <div className="bha-root">
        <Card padding="0">
          <Box padding="500" background="bg-surface-secondary">
            <BlockStack gap="0">
              {/* Header */}
              <InlineStack align="space-between" blockAlign="start" gap="400" wrap={false}>
                <BlockStack gap="200">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="h2" variant="headingLg">Blog content audit</Text>
                    <Badge tone="warning">Beta</Badge>
                    {running && (
                      <Badge tone="info" progress="partiallyComplete">Scanning</Badge>
                    )}
                  </InlineStack>

                  {!collapsed ? (
                    showTrackingBanner ? (
                    <Banner tone="info">
                      <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="medium">
                          Visitor tracking is active.
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          We're collecting your blog data and will email you when your first
                          performance insights are ready. No action needed.
                        </Text>
                      </BlockStack>
                    </Banner>
                    ) : null
                  ) : (
                    <InlineStack gap="200" blockAlign="center">
                      {running || animating ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {`Audit in progress · ${progressPercent}%`}
                        </Text>
                      ) : hasResults ? (
                        <>
                          <Badge tone={postsWithIssues.length > 0 ? "critical" : "success"}>
                            {`${postsWithIssues.length} ${postsWithIssues.length === 1 ? "post" : "posts"} with issues`}
                          </Badge>
                          <Badge tone="success">{`${issuesFixed} fixed`}</Badge>
                          <Badge>{`${passedChecks} of ${(audit?.checks || []).length} checks passed`}</Badge>

                        </>
                      ) : cancelled ? (
                        <Text as="span" variant="bodySm" tone="subdued">Audit cancelled</Text>
                      ) : (
                        <Text as="span" variant="bodySm" tone="subdued">No audit results yet</Text>
                      )}
                    </InlineStack>

                  )}
                </BlockStack>

                <InlineStack gap="200" blockAlign="center" wrap={false}>
                  {!collapsed && (complete || cancelled) && !animating && (
                    <Button onClick={restart} loading={busy} icon={RefreshIcon}>Rerun audit</Button>
                  )}
                  {!collapsed && canExport && (
                    <Button variant="primary" icon={ExportIcon} onClick={exportAudit}>Export</Button>
                  )}
                  <Button
                    icon={collapsed ? ChevronDownIcon : ChevronUpIcon}
                    onClick={toggleCollapsed}
                    ariaExpanded={!collapsed}
                    ariaControls="blog-audit-body"
                    accessibilityLabel={collapsed ? "Expand blog content audit" : "Collapse blog content audit"}
                  />
                </InlineStack>
              </InlineStack>

              {/* Error (stays visible when collapsed) */}
              {error && (
                <Box paddingBlockStart="500">
                  <Banner
                    tone="critical"
                    title="Unable to load audit"
                    action={{
                      content: "Try again",
                      onAction: () => setReloadKey((value) => value + 1),
                    }}
                  >
                    <Text as="p">{error}</Text>
                  </Banner>
                </Box>
              )}
              {/* Collapsed: summary cards shown below the header, full width */}
              {collapsed && hasResults && (
                <Box paddingBlockStart="500">
                  <InlineGrid columns={{ xs: 1, sm: 2, lg: "1fr 1fr 1fr 1.5fr" }} gap="400">
                    <SummaryCard
                      icon={AlertCircleIcon}
                      iconTone="critical"
                      iconBg="bg-surface-critical"
                      value={postsWithIssues.length}
                      label="Posts with issues"
                      helper={`out of ${scanned} total ${scanned === 1 ? "post" : "posts"}`}
                    />
                    <SummaryCard
                      icon={CheckCircleIcon}
                      iconTone="success"
                      iconBg="bg-surface-success"
                      value={issuesFixed}
                      label="Issues fixed"
                      helper="in this session"
                    />
                    <SummaryCard
                      icon={CheckCircleIcon}
                      iconTone="success"
                      iconBg="bg-surface-success"
                      value={passedChecks}
                      label="Passed checks"
                      helper={`of ${(audit?.checks || []).length} checks`}
                    />
                    <Box
                      padding="400"
                      background="bg-surface"
                      borderWidth="025"
                      borderColor="border"
                      borderRadius="300"
                    >
                      <BlockStack gap="300">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          Posts without issues
                        </Text>
                        <ProgressBar
                          progress={completionPercent}
                          size="small"
                          tone="highlight"
                          animated={false}
                        />
                        <InlineStack align="space-between" blockAlign="center" gap="200">
                          <Text as="p" variant="bodySm" tone="subdued">
                            {`${cleanPosts} of ${scanned} ${scanned === 1 ? "post has" : "posts have"} no issues`}
                          </Text>
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            {`${completionPercent}%`}
                          </Text>
                        </InlineStack>
                      </BlockStack>
                    </Box>
                  </InlineGrid>
                </Box>
              )}
              {/* Collapsible body */}
              <Collapsible
                open={!collapsed}
                id="blog-audit-body"
                transition={{ duration: "200ms", timingFunction: "ease-in-out" }}
              >
                <Box paddingBlockStart="500">
                  <BlockStack gap="500">
                    {/* Initial loading skeleton */}
                    {!audit && !error && (
                      <BlockStack gap="400">
                        <InlineStack gap="200" blockAlign="center">
                          <Spinner size="small" accessibilityLabel="Loading audit" />
                          <Text as="p" tone="subdued">
                            Loading your content audit…
                          </Text>
                        </InlineStack>
                        <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="400">
                          {[0, 1, 2, 3].map((i) => (
                            <Box
                              key={i}
                              padding="400"
                              background="bg-surface"
                              borderWidth="025"
                              borderColor="border"
                              borderRadius="300"
                            >
                              <BlockStack gap="300">
                                <SkeletonBodyText lines={1} />
                                <SkeletonDisplayText size="large" />
                              </BlockStack>
                            </Box>
                          ))}
                        </InlineGrid>
                      </BlockStack>
                    )}

                    {/* Running: step-by-step progress */}
                    {(running || animating) && (
                      <RunningAuditPanel
                        stepIndex={stepIndex}
                        percent={progressPercent}
                        etaText={complete || stepsDone ? "" : formatEta(etaSeconds)}
                        detail={complete || stepsDone ? "" : progressDetail}
                        done={stepsDone}
                        onCancel={running ? cancelAudit : undefined}
                        cancelling={cancelling}
                      />
                    )}

                    {/* Cancelled */}
                    {cancelled && (
                      <Banner
                        tone="warning"
                        title="Audit cancelled"
                        action={{
                          content: "Run audit again",
                          onAction: restart,
                          loading: busy,
                        }}
                      >
                        <Text as="p">The audit was stopped before it finished.</Text>
                      </Banner>
                    )}

                    {/* Failed */}
                    {audit?.status === "failed" && (
                      <Banner
                        tone="critical"
                        title={audit.error || "The audit could not finish"}
                        action={{
                          content: "Retry audit",
                          onAction: restart,
                          loading: busy,
                        }}
                      >
                        <Text as="p">Findings below cover only the posts checked so far.</Text>
                      </Banner>
                    )}

                    {/* Summary cards */}
                    {!animating && (complete || audit?.status === "failed") && scanned > 0 && (
                      <InlineGrid columns={{ xs: 1, sm: 2, lg: "1fr 1fr 1fr 1.5fr" }} gap="400">
                        <SummaryCard
                          icon={AlertCircleIcon}
                          iconTone="critical"
                          iconBg="bg-surface-critical"
                          value={postsWithIssues.length}
                          label="Posts with issues"
                          helper={`out of ${scanned} total ${scanned === 1 ? "post" : "posts"}`}
                        />
                        <SummaryCard
                          icon={CheckCircleIcon}
                          iconTone="success"
                          iconBg="bg-surface-success"
                          value={issuesFixed}
                          label="Issues fixed"
                          helper="in this session"
                        />
                        <SummaryCard
                          icon={CheckCircleIcon}
                          iconTone="success"
                          iconBg="bg-surface-success"
                          value={passedChecks}
                          label="Passed checks"
                          helper={`of ${(audit.checks || []).length} checks`}
                        />
                        <Box
                          padding="400"
                          background="bg-surface"
                          borderWidth="025"
                          borderColor="border"
                          borderRadius="300"
                        >
                          <BlockStack gap="300">
                            <Text as="p" variant="bodyMd" fontWeight="semibold">
                              Posts without issues
                            </Text>
                            <ProgressBar
                              progress={completionPercent}
                              size="small"
                              tone="highlight"
                              animated={false}
                            />
                            <InlineStack align="space-between" blockAlign="center" gap="200">
                              <Text as="p" variant="bodySm" tone="subdued">
                                {`${cleanPosts} of ${scanned} ${scanned === 1 ? "post has" : "posts have"} no issues`}
                              </Text>
                              <Text as="p" variant="bodySm" fontWeight="semibold">
                                {`${completionPercent}%`}
                              </Text>
                            </InlineStack>
                          </BlockStack>
                        </Box>
                      </InlineGrid>
                    )}

                    {/* Empty: no posts */}
                    {!animating && complete && scanned === 0 && (
                      <EmptyState heading="No published blog posts found" image={PLACEHOLDER_IMAGE}>
                        <Text as="p">
                          Publish a blog post, then run the audit again to see
                          recommendations.
                        </Text>
                      </EmptyState>
                    )}

                    {/* All clear */}
                    {!animating && complete && scanned > 0 && findings.length === 0 && (
                      <Banner tone="success" icon={CheckCircleIcon}>
                        <Text as="p" fontWeight="medium">
                          No issues found by these basic checks.
                        </Text>
                        <Text as="p" tone="subdued">
                          Your content is looking healthy. Run the audit again after
                          publishing new posts.
                        </Text>
                      </Banner>
                    )}

                    {/* All checks */}
                    {!running && !animating && allChecks.length > 0 && scanned > 0 && (
                      <BlockStack gap="400">
                        <Text as="h3" variant="headingMd">
                          All checks
                        </Text>

                        <div className="bha-toolbar">
                          <ButtonGroup variant="segmented" fullWidth={smDown}>
                            {[
                              ["all", `All (${allChecks.length})`],
                              ["issues", `Issues (${findings.length})`],
                              ["passed", `Passed (${passedChecks})`],
                            ].map(([value, label]) => (
                              <Button
                                key={value}
                                pressed={statusFilter === value}
                                onClick={() => setStatusFilter(value)}
                              >
                                {label}
                              </Button>
                            ))}
                          </ButtonGroup>

                          <div className="bha-toolbar-right">
                            <div className="bha-search">
                              <TextField
                                label="Search posts"
                                labelHidden
                                placeholder="Search by post or blog title"
                                value={query}
                                onChange={handleQueryChange}
                                prefix={<Icon source={SearchIcon} />}
                                clearButton
                                onClearButtonClick={() => handleQueryChange("")}
                                autoComplete="off"
                              />
                            </div>
                            <div className="bha-sort">
                              <Select
                                label="Sort"
                                labelHidden
                                options={SORT_OPTIONS}
                                value={sort}
                                onChange={setSort}
                              />
                            </div>
                          </div>
                        </div>

                        <BlockStack gap="300">
                          {visibleChecks.map((check) => {
                            const selected = selectedCheck === check.key;
                            return (
                              <CheckRow
                                key={check.key}
                                check={check}
                                total={scanned}
                                selected={selected}
                                onToggle={() => selectCheck(selected ? "" : check.key)}
                              >
                                {selected && selectedDefinition && (
                                  <AffectedPostsPanel
                                    definition={selectedDefinition}
                                    checkPostCount={checkPosts.length}
                                    reviewedCount={reviewedCount}
                                    posts={affectedPosts}
                                    visiblePosts={visiblePosts}
                                    remaining={remaining}
                                    query={query}
                                    onQueryChange={handleQueryChange}
                                    labels={labels}
                                    issueKey={selectedCheck}
                                    expandedPost={expandedPost}
                                    onToggleExpanded={(id) =>
                                      setExpandedPost(expandedPost === id ? null : id)
                                    }
                                    reviewed={reviewed}
                                    onToggleReviewed={toggleReviewed}
                                    onShowMore={() =>
                                      setVisibleCount((count) => count + PAGE_SIZE)
                                    }
                                  />
                                )}
                              </CheckRow>
                            );
                          })}
                          {visibleChecks.length === 0 && (
                            <Text as="p" tone="subdued">
                              No checks match your search or filter.
                            </Text>
                          )}
                        </BlockStack>
                      </BlockStack>
                    )}
                  </BlockStack>
                </Box>
              </Collapsible>
            </BlockStack>
          </Box>

          {/* Footer (hidden when collapsed) */}
          {!collapsed && audit?.completed_at && (
            <>
              <Divider />
              <Box padding="400" background="bg-surface">
                <InlineStack align="space-between" blockAlign="center" gap="300">
                  <Text as="p" tone="subdued" variant="bodySm">
                    Last audit:{" "}
                    {new Date(audit.completed_at).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </Text>
                  <Link url="https://help.shopify.com/en/manual/online-store/blogs" external>
                    Blog best practices
                  </Link>
                </InlineStack>
              </Box>
            </>
          )}
        </Card>
      </div>
    </Box>
  );
}